// --- PNG DPI Manipulation Utility ---
const crcTable = new Int32Array(256);
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let j = 0; j < 8; j++) {
    if (c & 1) {
      c = 0xedb88320 ^ (c >>> 1);
    } else {
      c = c >>> 1;
    }
  }
  crcTable[i] = c;
}

export function crc32(buf: Uint8Array): number {
  let c = -1;
  for (let i = 0; i < buf.length; i++) {
    c = crcTable[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
  }
  return c ^ -1;
}

/**
 * Insert (or update) a pHYs chunk carrying the print resolution, on raw PNG bytes.
 *
 * Works in bytes rather than base64 because callers now hold PNG bytes: encoding
 * to base64 and back is expensive and, for a large upscale, allocates several
 * times the size of the image in strings.
 */
export function setDpiInPngBytes(buf: Uint8Array, dpi: number): Uint8Array {
  if (dpi <= 0) return buf;

  const ppm = Math.round((dpi / 2.54) * 100);

  let offset = 8;
  while (offset + 8 <= buf.length) {
    const length =
      (buf[offset] << 24) | (buf[offset + 1] << 16) | (buf[offset + 2] << 8) | buf[offset + 3];
    const type = String.fromCharCode(
      buf[offset + 4],
      buf[offset + 5],
      buf[offset + 6],
      buf[offset + 7]
    );

    if (type === 'IHDR') {
      const nextOffset = offset + 12 + length;
      const out = new Uint8Array(buf.length + 21);
      out.set(buf.subarray(0, nextOffset), 0);

      const phys = new Uint8Array(21);
      phys[3] = 9;
      phys[4] = 112; phys[5] = 72; phys[6] = 89; phys[7] = 115;

      // x and y pixels-per-metre, both big-endian.
      phys[8] = (ppm >>> 24) & 0xff; phys[9] = (ppm >>> 16) & 0xff;
      phys[10] = (ppm >>> 8) & 0xff; phys[11] = ppm & 0xff;
      phys[12] = (ppm >>> 24) & 0xff; phys[13] = (ppm >>> 16) & 0xff;
      phys[14] = (ppm >>> 8) & 0xff; phys[15] = ppm & 0xff;
      phys[16] = 1; // unit: metres

      const crc = crc32(phys.subarray(4, 17));
      phys[17] = (crc >>> 24) & 0xff;
      phys[18] = (crc >>> 16) & 0xff;
      phys[19] = (crc >>> 8) & 0xff;
      phys[20] = crc & 0xff;

      out.set(phys, nextOffset);
      out.set(buf.subarray(nextOffset), nextOffset + 21);
      return out;
    }
    offset += 12 + length;
  }
  return buf;
}

/** Base64 convenience wrapper, kept for callers that only have a data URL. */
export function setDpiInPngBase64(base64: string, dpi: number): string {
  if (dpi <= 0) return base64;

  const binary = atob(base64.split(',')[1]);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);

  const stamped = setDpiInPngBytes(buf, dpi);
  if (stamped === buf) return base64;

  let out = '';
  // Chunked to avoid exceeding the argument limit on large images.
  const CHUNK = 0x8000;
  for (let i = 0; i < stamped.length; i += CHUNK) {
    out += String.fromCharCode.apply(null, Array.from(stamped.subarray(i, i + CHUNK)));
  }
  return 'data:image/png;base64,' + btoa(out);
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

interface BgColorModel {
  type: 'solid' | 'gradient';
  c1: { r: number; g: number; b: number };
  c2: { r: number; g: number; b: number };
}

export function getBackgroundModel(data: Uint8ClampedArray, width: number, height: number): BgColorModel {
  const borderPixels: { r: number; g: number; b: number }[] = [];
  const inset = 2;

  for (let x = inset; x < width - inset; x += 4) {
    borderPixels.push(getPixel(data, width, x, inset));
    borderPixels.push(getPixel(data, width, x, height - 1 - inset));
  }
  for (let y = inset; y < height - inset; y += 4) {
    borderPixels.push(getPixel(data, width, inset, y));
    borderPixels.push(getPixel(data, width, width - 1 - inset, y));
  }

  let c1 = { r: 255, g: 255, b: 255 };
  let c2 = { r: 230, g: 230, b: 230 };

  if (borderPixels.length > 0) {
    const sorted = borderPixels.map(p => ({
      p,
      l: p.r * 0.299 + p.g * 0.587 + p.b * 0.114
    })).sort((a, b) => a.l - b.l);

    // Use the 10th to 90th percentile to avoid outliers (like dark borders)
    const start = Math.floor(sorted.length * 0.1);
    const end = Math.floor(sorted.length * 0.9);
    const core = sorted.slice(start, end);

    if (core.length > 10) {
      const darkPart = core.slice(0, Math.floor(core.length * 0.25));
      const lightPart = core.slice(Math.floor(core.length * 0.75));

      const avg = (arr: typeof core) => {
        let r = 0, g = 0, b = 0;
        for (const item of arr) { r += item.p.r; g += item.p.g; b += item.p.b; }
        return {
          r: Math.round(r / arr.length),
          g: Math.round(g / arr.length),
          b: Math.round(b / arr.length)
        };
      };

      c1 = avg(lightPart);
      c2 = avg(darkPart);
    }
  }

  const distSq = (c1.r - c2.r) ** 2 + (c1.g - c2.g) ** 2 + (c1.b - c2.b) ** 2;
  if (distSq < 25) { // Colors are very similar, treat as solid
    return { type: 'solid', c1, c2: c1 };
  }

  return { type: 'gradient', c1, c2 };
}

export function getPixel(data: Uint8ClampedArray, width: number, x: number, y: number) {
  const idx = (y * width + x) * 4;
  return { r: data[idx], g: data[idx + 1], b: data[idx + 2], a: data[idx + 3] };
}
// ------------------------------------